import { Link } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ARTICLES, CATEGORIES } from "@/data/articles";

/**
 * /articles — index / listing page for the GEO content library.
 * Groups by category chip filter (all / buying-guide / workflow /
 * comparison / craft). Each card links to /articles/:slug.
 *
 * SEO note: Google was crawling `/articles?q={search_term_string}`
 * literally from our WebSite JSON-LD SearchAction template — plus any
 * query-string variant we ever produce. We emit a hard canonical to
 * `/articles` here and mark query-string variants as `noindex,follow`
 * so search-result-like URLs consolidate onto the canonical index.
 */
export default function Articles() {
  const [filter, setFilter] = useState("all");
  const hasQuery =
    typeof window !== "undefined" && window.location.search.length > 0;

  const shown = filter === "all" ? ARTICLES : ARTICLES.filter((a) => a.category === filter);

  useEffect(() => {
    const prev = document.title;
    document.title = "Articles — Facetra";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden" data-testid="page-articles">
      <Helmet>
        <link rel="canonical" href="https://facetra.studio/articles" />
        {hasQuery && <meta name="robots" content="noindex,follow" />}
        <title>Articles — Facetra</title>
        <meta
          name="description"
          content="Long-form guides on AI jewellery design, sketch-to-CAD workflow, BOM at Zaveri Bazaar rate, Kundan / Jadau / Polki / Diamond / Temple / Meenakari craft references."
        />
      </Helmet>
      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/"
            data-testid="articles-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </header>

      <main className="relative max-w-5xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
          Facetra · Library
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Deep-dives for the working atelier.
        </h1>
        <p className="mt-6 text-white/60 text-sm md:text-base leading-relaxed max-w-2xl">
          Practical, opinionated writing on Indian jewellery design, CAD workflow,
          AI-assisted rendering, and the craft vocabulary that separates a manufacturable
          reference from a pretty picture.
        </p>

        {/* Filter chips */}
        <div className="mt-10 flex flex-wrap items-center gap-2 not-prose">
          <Chip active={filter === "all"} onClick={() => setFilter("all")} label="All" testId="filter-all" count={ARTICLES.length} />
          {Object.entries(CATEGORIES).map(([k, c]) => {
            const count = ARTICLES.filter((a) => a.category === k).length;
            return (
              <Chip
                key={k}
                active={filter === k}
                onClick={() => setFilter(k)}
                label={c.label}
                testId={`filter-${k}`}
                count={count}
              />
            );
          })}
        </div>

        {/* Article cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {shown.map((a) => (
            <Link
              key={a.slug}
              to={`/articles/${a.slug}`}
              data-testid={`article-card-${a.slug}`}
              className="group rounded-2xl border border-white/8 bg-[#0E0E0E] p-6 hover:border-[#D4AF37]/30 transition"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80">
                  {CATEGORIES[a.category]?.label}
                </span>
                <span className="text-[10px] text-white/35 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {a.readMinutes} min
                </span>
              </div>
              <h2 className="font-display text-[19px] leading-snug text-white group-hover:text-[#D4AF37] transition">
                {a.title}
              </h2>
              <p className="mt-3 text-[13px] text-white/55 leading-relaxed line-clamp-3">
                {a.tldr}
              </p>
              <div className="mt-4 text-[11px] text-white/40 inline-flex items-center gap-1">
                Read <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}

function Chip({ active, onClick, label, count, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11.5px] transition " +
        (active
          ? "bg-[#D4AF37] text-black font-medium"
          : "border border-white/10 text-white/70 hover:border-[#D4AF37]/40 hover:text-white")
      }
    >
      {label}
      <span className={"tabular-nums text-[10px] " + (active ? "opacity-70" : "opacity-50")}>
        {count}
      </span>
    </button>
  );
}
